import { createContext, type ReactNode, useMemo } from 'react';
import { useDefinedContext } from '@shared/hooks/useDefinedContext';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { useOverlay } from '@shared/hooks/useOverlay';

interface DrawerContextProviderProps {
  children: ReactNode;
}

export interface DrawerContextState {
  closeDrawer: () => void;
  isOpen: boolean;
  openDrawer: () => void;
}

export const DrawerContext = createContext<DrawerContextState | null>(null);
DrawerContext.displayName = 'Drawer';

export const useDrawerContext = () => useDefinedContext(DrawerContext);

export const DrawerContextProvider = ({
  children,
}: DrawerContextProviderProps) => {
  const { isOpen, open: openDrawer, close: closeDrawer } = useIsOpen();

  useOverlay(isOpen);

  const value = useMemo<DrawerContextState>(
    () => ({
      closeDrawer,
      isOpen,
      openDrawer,
    }),
    [isOpen],
  );

  return (
    <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>
  );
};
